import { useCallback, useEffect, useRef, useState } from 'react'
import type { GameViewProps } from '../../core/contracts'
import { haptics } from '../../platform/telegram'
import { useT } from '../../platform/i18n/useT'
import { Shape } from './Shape'
import { missingShapeOf } from './logic'
import type { MissingAnswer, MissingRound, ShapeId } from './logic'
import './view.css'

/**
 * Экран Missing One.
 *
 * На каждый набор: показ -> пустое место -> выбор варианта.
 * Ответы копим и отдаём движку пакетом в конце раунда.
 */

type Phase = 'show' | 'recall' | 'flash'

export function MissingOneView({
  round,
  level,
  onAnswer,
}: GameViewProps<MissingRound, MissingAnswer>) {
  const { t } = useT()
  const [index, setIndex] = useState(0)
  const [phase, setPhase] = useState<Phase>('show')
  const [hit, setHit] = useState(false)

  const picks = useRef<(ShapeId | null)[]>([])
  const times = useRef<number[]>([])
  const recallStartedAt = useRef(0)

  const trial = round.trials[index]

  const finish = useCallback(() => {
    const ok = times.current
    const rtMs = ok.length > 0 ? Math.round(ok.reduce((a, b) => a + b, 0) / ok.length) : 0
    onAnswer({ picks: picks.current, rtMs })
  }, [onAnswer])

  // показ набора
  useEffect(() => {
    if (phase !== 'show' || !trial) return
    setPhase('show')
    const id = setTimeout(() => {
      recallStartedAt.current = performance.now()
      setPhase('recall')
    }, trial.showMs)
    return () => clearTimeout(id)
  }, [phase, trial])

  const pick = useCallback(
    (shape: ShapeId) => {
      if (phase !== 'recall' || !trial) return

      const rt = performance.now() - recallStartedAt.current
      const correct = shape === missingShapeOf(trial)

      picks.current.push(shape)
      if (correct) times.current.push(rt)

      if (correct) haptics.correct()
      else haptics.wrong()

      setHit(correct)
      setPhase('flash')

      setTimeout(() => {
        if (index + 1 >= round.trials.length) {
          finish()
        } else {
          setIndex((i) => i + 1)
          setPhase('show')
        }
      }, 700)
    },
    [phase, trial, index, round.trials.length, finish],
  )

  if (!trial) return null

  const showingSet = phase === 'show'

  return (
    <div className="mo">
      <div className="mo__top">
        <span>{t('game.level', { n: level })}</span>
        <span>
          {index + 1} / {round.trials.length}
        </span>
      </div>

      <p className="mo__hint">
        {showingSet ? t('game.missingOne.study') : t('game.missingOne.hint')}
      </p>

      <div className="mo__set">
        {trial.set.map((shape, i) => {
          const empty = !showingSet && i === trial.missingIndex
          return (
            <div key={i} className={empty ? 'mo__slot mo__slot--empty' : 'mo__slot'}>
              {!empty && <Shape id={shape} />}
            </div>
          )
        })}
      </div>

      <div className={phase === 'flash' ? `mo__options mo__options--${hit ? 'hit' : 'miss'}` : 'mo__options'}>
        {trial.options.map((shape) => (
          <button
            key={shape}
            type="button"
            className="mo__opt"
            onClick={() => pick(shape)}
            disabled={phase !== 'recall'}
          >
            <Shape id={shape} />
          </button>
        ))}
      </div>
    </div>
  )
}
